// WunschHoverCard — schöne Hover-Anzeige eines Dienstwunsches in der Matrix.
import React from 'react';
import { Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y.slice(-2)}`;
  } catch { return iso; }
};

export default function WunschHoverCard({ wunsch, children, openModal, canEditWunsch }) {
  if (!wunsch) return children;
  const isAbgelehnt = wunsch.status === 'ABGELEHNT';

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-80 p-3" side="top" align="start">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            {isAbgelehnt ? (
              <AlertCircle className="w-4 h-4 text-stone-500" />
            ) : (
              <Sparkles className="w-4 h-4 text-orange-500" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wider">
              Dienstwunsch
            </span>
          </div>
          {isAbgelehnt && (
            <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50 text-[10px]">
              Abgelehnt
            </Badge>
          )}
        </div>

        <div className="space-y-1.5 text-sm">
          <p className="font-semibold">{wunsch.mitarbeiter_name || 'Mitarbeiter'}</p>
          <p className="text-xs text-muted-foreground">
            {formatDate(wunsch.datum)} · {wunsch.dienst_art === 'N' ? 'Nacht' : 'Tag'}
          </p>
          {wunsch.fahrzeug_name && (
            <p className="text-xs">
              <span className="text-muted-foreground">Fahrzeug:</span>{' '}
              <span className="font-mono font-semibold">{wunsch.fahrzeug_name}</span>
            </p>
          )}
          {wunsch.notiz && (
            <div className="mt-2 rounded-md border bg-muted/40 p-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Begründung</p>
              <p className="text-xs whitespace-pre-wrap break-words">{wunsch.notiz}</p>
            </div>
          )}
          {wunsch.einteiler_notiz && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/50 p-2">
              <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider mb-0.5">Einteiler-Notiz</p>
              <p className="text-xs whitespace-pre-wrap break-words text-amber-900">{wunsch.einteiler_notiz}</p>
            </div>
          )}
        </div>

        {openModal && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openModal(); }}
            className={cn(
              "mt-3 w-full text-xs rounded-md border px-2 py-1.5 transition-colors",
              canEditWunsch
                ? "border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-800"
                : "border-stone-300 hover:bg-stone-50"
            )}
            data-testid="wunsch-open-modal-btn"
          >
            {canEditWunsch ? 'Bearbeiten / Ablehnen' : 'Details öffnen'}
          </button>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
